import { useState, useEffect } from 'react';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';

/**
 * Hook customizado para gerenciar permissões do usuário logado
 * 
 * Retorna:
 * - permissoes: objeto com todas as permissões do usuário
 * - loading: se está carregando as permissões
 * - error: erro ao carregar
 * - podeVisualizar(recurso): verifica se pode visualizar
 * - podeCriar(recurso): verifica se pode criar
 * - podeEditar(recurso): verifica se pode editar
 * - podeExcluir(recurso): verifica se pode excluir
 * - podeValidar(recurso): verifica se pode validar
 * - podeExportar(recurso): verifica se pode exportar
 * - temQualquerPermissao(recurso): verifica se tem alguma permissão
 * - recarregar(): recarrega as permissões
 */
export const usePermissoes = () => {
  const { user } = useAuth();
  const [permissoes, setPermissoes] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    console.log('[usePermissoes] loading:', loading);
  }, [loading]);

  const carregarPermissoes = async () => {
    try {
      setLoading(true);
      if (!user) {
        setPermissoes(null);
        setError(null);
        return;
      }

      const role = (user?.role || user?.perfil || '').toString().trim().toUpperCase();
      if (role === 'ADMIN') {
        setPermissoes({
          perfil_id: user?.id_perfil || 0,
          perfil_nome: role,
          is_admin: true,
          recursos: {},
        });
        setError(null);
        return;
      }

      if (role === 'RESPONSAVEL') {
        setPermissoes({
          perfil_id: user?.id_perfil || 0,
          perfil_nome: role,
          is_admin: false,
          recursos: {},
        });
        setError(null);
        return;
      }

      const response = await api.get('/api/permissoes/minhas');

      console.log('[usePermissoes] response.data:', response.data);
      console.log('[usePermissoes] response.data.is_admin:', response.data?.is_admin);
      console.log('[usePermissoes] response.data.recursos:', response.data?.recursos);
      setPermissoes(response.data);
      setError(null);
    } catch (err) {
      console.error('[usePermissoes] erro ao carregar permissoes', err);
      setError(err.message);
      setPermissoes(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    carregarPermissoes();
  }, [user]);

  // Helper functions
  const obterPermissaoRecurso = (nomeRecurso) => {
    if (!permissoes || !permissoes.recursos) return null;
    return permissoes.recursos[nomeRecurso];
  };

  const podeVisualizar = (nomeRecurso) => {
    if (permissoes?.is_admin) return true;
    const perm = obterPermissaoRecurso(nomeRecurso);
    return perm?.pode_visualizar === true;
  };

  const podeCriar = (nomeRecurso) => {
    if (permissoes?.is_admin) return true;
    const perm = obterPermissaoRecurso(nomeRecurso);
    return perm?.pode_criar === true;
  };

  const podeEditar = (nomeRecurso) => {
    if (permissoes?.is_admin) return true;
    const perm = obterPermissaoRecurso(nomeRecurso);
    return perm?.pode_editar === true;
  };

  const podeExcluir = (nomeRecurso) => {
    if (permissoes?.is_admin) return true;
    const perm = obterPermissaoRecurso(nomeRecurso);
    return perm?.pode_excluir === true;
  };

  const podeValidar = (nomeRecurso) => {
    if (permissoes?.is_admin) return true;
    const perm = obterPermissaoRecurso(nomeRecurso);
    return perm?.pode_validar === true;
  };

  const podeExportar = (nomeRecurso) => {
    if (permissoes?.is_admin) return true;
    const perm = obterPermissaoRecurso(nomeRecurso);
    return perm?.pode_exportar === true;
  };

  const temQualquerPermissao = (nomeRecurso) => {
    if (permissoes?.is_admin) return true;
    const perm = obterPermissaoRecurso(nomeRecurso);
    if (!perm) return false;
    
    return perm.pode_visualizar ||
           perm.pode_criar ||
           perm.pode_editar ||
           perm.pode_excluir ||
           perm.pode_validar ||
           perm.pode_exportar;
  };

  const apenasPropriosRegistros = (nomeRecurso) => {
    if (permissoes?.is_admin) return false;
    const perm = obterPermissaoRecurso(nomeRecurso);
    return perm?.apenas_proprios === true;
  };

  const apenasUnidade = (nomeRecurso) => {
    if (permissoes?.is_admin) return false;
    const perm = obterPermissaoRecurso(nomeRecurso);
    return perm?.apenas_unidade === true;
  };

  return {
    permissoes,
    loading,
    error,
    isAdmin: permissoes?.is_admin || false,
    perfilNome: permissoes?.perfil_nome || '',
    podeVisualizar,
    podeCriar,
    podeEditar,
    podeExcluir,
    podeValidar,
    podeExportar,
    temQualquerPermissao,
    apenasPropriosRegistros,
    apenasUnidade,
    recarregar: carregarPermissoes
  };
};

/**
 * Hook simplificado para verificar permissão de um recurso específico
 * 
 * @param {string} nomeRecurso - Nome do recurso (ex: 'aulas_dadas', 'receitas')
 * @returns Objeto com todas as verificações de permissão para o recurso
 */
export const usePermissaoRecurso = (nomeRecurso) => {
  const {
    permissoes,
    loading,
    isAdmin,
    podeVisualizar,
    podeCriar,
    podeEditar,
    podeExcluir,
    podeValidar,
    podeExportar,
    temQualquerPermissao,
    apenasPropriosRegistros,
    apenasUnidade
  } = usePermissoes();

  return {
    loading,
    isAdmin,
    permissoes: permissoes?.recursos?.[nomeRecurso] || null,
    podeVisualizar: podeVisualizar(nomeRecurso),
    podeCriar: podeCriar(nomeRecurso),
    podeEditar: podeEditar(nomeRecurso),
    podeExcluir: podeExcluir(nomeRecurso),
    podeValidar: podeValidar(nomeRecurso),
    podeExportar: podeExportar(nomeRecurso),
    temAcesso: temQualquerPermissao(nomeRecurso),
    apenasPropriosRegistros: apenasPropriosRegistros(nomeRecurso),
    apenasUnidade: apenasUnidade(nomeRecurso)
  };
};

export default usePermissoes;
